<template>
  <Teleport :to="props.to">
    <div
      v-if="isOpenWrapper"
      :class="[
        $style['d-modal'],
        {
          [$style['d-modal_size-big']]: props.size === 'big',
          [$style['d-modal_size-small']]: props.size === 'small',
          [$style['d-modal_size-full']]: props.size === 'full',
        },
        props.classList,
      ]"
      :style="props.styleList"
    >
      <div
        ref="overlayRef"
        :class="$style['d-modal__overlay']"
        :style="{
          zIndex: zIndex,
        }"
        @click="onBgClick"
      />
      <div
        ref="contentRef"
        :class="[
          $style['d-modal__content'],
        ]"
        :style="{
          zIndex: zIndex,
        }"
      >
        <slot />
      </div>
    </div>
  </Teleport>
</template>
<script setup>
import { ref, watch, nextTick, onMounted } from 'vue';
import anime from 'animejs';
import { useId } from '../../hooks/useId';

defineOptions({
  name: 'modal',
  compatConfig: {
    MODE: 3,
  },
});

const props = defineProps({
  modelValue: Boolean,
  name: {
    type: String,
    default: () => useId(),
  },
  delay: {
    type: Number,
    default: 500,
  },
  /** @type {'big'|'small'|'full'} */
  size: {
    type: String,
    default: 'big',
  },
  classList: [String, Object, Array],
  styleList: Object,
  zIndex: Number,
  to: {
    type: String,
    default: '#app',
  },
  /** @deprecated */
  title: String,
  /** @deprecated */
  type: String,
  /** @deprecated */
  mobileFull: Boolean,
  /** @deprecated */
  disableContainer: Boolean,
  /** deprecated */
  dark: Boolean,
  persistent: Boolean,
});

const emit = defineEmits(['update:modelValue', 'opened', 'before-close', 'closed', 'before-open', ]);

const isOpenWrapper = ref(props.modelValue);
const localValue = ref(props.modelValue);

watch(() => props.modelValue, (val) => {
  if (val === localValue.value) return;

  localValue.value = val;
});

const contentRef = ref(null);
const overlayRef = ref(null);

onMounted(() => {
  if (contentRef.value && overlayRef.value) {
    contentRef.value.style.opacity = 1;
    overlayRef.value.style.opacity = 1;
  }
});

const cssVars = {
  topOffset: '100px',
};

const easing = 'easeInOutCubic';

const open = async () => {
  emit('before-open');
  isOpenWrapper.value = true;
  await nextTick();
  anime.remove([contentRef.value, overlayRef.value, cssVars]);
  anime({
    targets: [contentRef.value, overlayRef.value],
    opacity: 1,
    easing,
    duration: props.delay,
    complete: () => {
      emit('opened');
    },
  });
  anime({
    targets: [cssVars],
    topOffset: '0px',
    easing,
    duration: props.delay,
    update: () => {
      if (contentRef.value) contentRef.value.style.setProperty('--top-offset', cssVars.topOffset)
    },
  });
};

const close = () => {
  emit('before-close');
  anime.remove([contentRef.value, overlayRef.value, cssVars]);
  anime({
    targets: [contentRef.value, overlayRef.value],
    opacity: 0,
    easing,
    duration: props.delay,
    complete: () => {
      isOpenWrapper.value = false;
      emit('closed');
    },
  });
  anime({
    targets: [cssVars],
    topOffset: '100px',
    easing,
    duration: props.delay,
    update: () => {
      if (contentRef.value) contentRef.value.style.setProperty('--top-offset', cssVars.topOffset)
    },
  });
};

watch(localValue, (val) => {
  emit('update:modelValue', val);

  if (val) {
    open();
  } else {
    close();
  }
});

const onBgClick = () => {
  if (props.persistent) return;

  localValue.value = false;
};

</script>
<style lang="scss" module>
@import "../../assets/scss/base/variables";
@import "../../assets/scss/base/functions";
@import "../../assets/scss/base/mixins";

.d-modal {
  box-sizing: border-box;
  --content-max-height: calc(var(--app-vh, 100vh) * 0.87);
  --content-width: auto;
  --content-height: auto;

  &_size {
    &-extra {
      @include fluid('--content-width', 1216px);
    }

    &-big {
      @include fluid('--content-width', 1008px);
    }

    &-small {
      @include fluid('--content-width', 592px);
    }

    &-full {
      --content-width: 100%;
      --content-height: var(--app-vh, 100vh);
      --content-max-height: var(--app-vh, 100vh);
    }
  }

  &__content {
    position: fixed;
    box-sizing: border-box;
    opacity: 0;
    top: 50%;
    left: 50%;
    max-width: 100vw;
    max-height: var(--content-max-height);
    transform: translate(-50%, calc(-50% + var(--top-offset)));
    width: var(--content-width);
    height: var(--content-height);
    display: flex;
    flex-direction: column;
  }

  &__overlay {
    position: fixed;
    left: 0;
    top: 0;
    width: 100vw;
    height: var(--app-vh);
    opacity: 0;
    background: #252628bf;
  }
}
</style>
